/**
 * PATH: packages/ui/src/live-toasts/LiveToastStack.tsx
 * FILE_ID: UI-LIVE-TOASTS-STACK-001
 * PURPOSE: The toast stack every shell mounts once: newest toast nearest the
 *          anchor, three shown, the rest behind "+N more", Clear all, and the
 *          sound switch. Chimes once per burst of new toasts unless muted.
 * LAYOUT: top-right under the header on desktop, because the bottom-right
 *          belongs to the master BottomDock; above --mobile-dock-h on phones.
 * LANDMINE: carries data-live-toasts so dialogs treat a click here as inside
 *          the toast layer (lib/toastLayer.ts) and stay open.
 */
import { useCallback, useEffect, useState } from 'react'
import { Volume2, VolumeX } from 'lucide-react'
import { zIndex } from '../theme'
import { armChimeUnlock, playChime, setChimeMuted, useChimeMuted } from './chime'
import { LiveToastCard } from './LiveToastCard'
import {
  clearLiveToasts,
  dismissLiveToast,
  onLiveToastPushed,
  runLiveToastAction,
  useLiveToasts,
  type LiveToastAction,
} from './liveToastStore'

export interface LiveToastStackProps {
  /** Host router for action links. Without it links open as a page load. */
  onNavigate?: (href: string) => void
  maxVisible?: number
}

const CONTROL = 'rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground'

function useToastChime(): void {
  useEffect(() => armChimeUnlock(), [])
  useEffect(
    () =>
      onLiveToastPushed((toast) => {
        if (toast.sound !== false) playChime(toast.tone)
      }),
    [],
  )
}

export function LiveToastStack({ onNavigate, maxVisible = 3 }: LiveToastStackProps) {
  const toasts = useLiveToasts()
  const muted = useChimeMuted()
  const [expanded, setExpanded] = useState(false)
  useToastChime()
  const navigate = useCallback(
    (href: string) => (onNavigate ? onNavigate(href) : window.location.assign(href)),
    [onNavigate],
  )

  if (toasts.length === 0) return null
  const shown = expanded ? toasts : toasts.slice(0, maxVisible)
  const hidden = toasts.length - shown.length

  return (
    <section
      data-live-toasts=""
      aria-label="Notifications"
      aria-live="polite"
      style={{ zIndex: zIndex.toast }}
      className="pointer-events-none fixed bottom-[calc(var(--mobile-dock-h,0px)+1rem)] right-4 flex max-h-[calc(100dvh-8rem)] w-[min(360px,calc(100vw-2rem))] flex-col-reverse gap-2 overflow-y-auto md:bottom-auto md:top-16 md:flex-col"
    >
      {shown.map((toast) => (
        <LiveToastCard
          key={toast.id}
          toast={toast}
          onAction={(action: LiveToastAction) => void runLiveToastAction(toast.id, action, navigate)}
          onClose={() => dismissLiveToast(toast.id)}
        />
      ))}
      <div className="pointer-events-auto flex items-center justify-end gap-1 rounded-md bg-popover/90 px-1 py-0.5 shadow-sm backdrop-blur">
        {(hidden > 0 || expanded) && toasts.length > maxVisible && (
          <button type="button" className={CONTROL} onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Show fewer' : `+${hidden} more`}
          </button>
        )}
        {toasts.length > 1 && (
          <button type="button" className={CONTROL} onClick={() => { clearLiveToasts(); setExpanded(false) }}>
            Clear all
          </button>
        )}
        <button
          type="button"
          aria-pressed={muted}
          aria-label={muted ? 'Turn toast sounds on' : 'Mute toast sounds'}
          title={muted ? 'Sounds off' : 'Sounds on'}
          className={CONTROL}
          onClick={() => setChimeMuted(!muted)}
        >
          {muted ? <VolumeX className="h-3.5 w-3.5" aria-hidden /> : <Volume2 className="h-3.5 w-3.5" aria-hidden />}
        </button>
      </div>
    </section>
  )
}
